package com.wintermar.inspection.feature.presentation.inspection.complete

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.ui.component.image.util.FileDownloader
import com.wintermar.inspection.core.ui.component.image.util.FileMimeType
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarController
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarVariant
import com.wintermar.inspection.feature.domain.usecase.inspection.ApproveInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.DeleteInspectionReportUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.DeleteInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.DownloadInspectionReportUseCase
import com.wintermar.inspection.util.Constant
import kotlinx.coroutines.NonCancellable
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext

class InspectionCompleteViewModel(
    private val inspectionId: String,
    private val approveInspectionUseCase: ApproveInspectionUseCase,
    private val deleteInspectionUseCase: DeleteInspectionUseCase,
    private val downloadInspectionReportUseCase: DownloadInspectionReportUseCase,
    private val deleteInspectionReportUseCase: DeleteInspectionReportUseCase
) : ViewModel() {
    private val _navigationEvent = Channel<InspectionCompleteEvent>()
    val navigationEvent = _navigationEvent.receiveAsFlow()

    val state = InspectionCompleteState(
        onApproveInspection = {
            approveInspection(inspectionId)
        },
        onDownloadInspectionReport = {
            downloadPdf()
        },
        onEditInspection = {
            viewModelScope.launch {
                handleOnEditInspection()
            }
        }
    )

    private fun approveInspection(inspectionId: String) {
        approveInspectionUseCase(inspectionId).onEach { result ->
            when (result) {
                is FlowResult.Loading -> state.isLoading = result.showLoading
                is FlowResult.Error -> showSnackBar(
                    message = result.error?.message
                )

                is FlowResult.Success -> processAfterApprove(
                    inspectionId = inspectionId,
                    onNavigateToLanding = { onNavigateToLanding() },
                    onDeleteInspection = { deleteInspection(it) }
                )
            }
        }.launchIn(viewModelScope)
    }

    private fun deleteInspection(inspectionId: String) {
        deleteInspectionUseCase(inspectionId, deleteLocalOnly = true).onEach { result ->
            when (result) {
                is FlowResult.Loading -> state.isLoading = result.showLoading
                is FlowResult.Success -> onNavigateToLanding()
                is FlowResult.Error -> showSnackBar(
                    message = result.error?.message
                )
            }
        }.launchIn(viewModelScope)
    }

    private suspend fun onNavigateToLanding() {
        _navigationEvent.send(
            InspectionCompleteEvent.NavigateToLanding(
                inspectionId = inspectionId
            )
        )
    }

    //Concurrent Execution (Fire-and-Forget)
    private suspend fun handleOnEditInspection() {
        viewModelScope.launch {
            withContext(NonCancellable) {
                deleteInspectionReportUseCase(inspectionId).collect()
            }
        }
        _navigationEvent.send(
            InspectionCompleteEvent.NavigateToInspectionAnswer
        )
    }

    private fun downloadPdf() {
        downloadInspectionReportUseCase(inspectionId).onEach { result ->
            when (result) {
                is FlowResult.Error -> showSnackBar(
                    message = result.error?.message
                )

                is FlowResult.Loading -> state.isLoading = result.showLoading
                is FlowResult.Success -> result.data?.let {
                    FileDownloader.download(it, FileMimeType.PDF)
                }
            }
        }.launchIn(viewModelScope)
    }

    private suspend fun showSnackBar(
        message: String?,
        variant: SnackBarVariant = SnackBarVariant.ERROR
    ) {
        SnackBarController.show(
            message = message ?: Constant.UNKNOWN_ERROR,
            variant = variant
        )
    }
}

expect suspend fun processAfterApprove(
    inspectionId: String,
    onNavigateToLanding: suspend () -> Unit,
    onDeleteInspection: suspend (String) -> Unit
)
